import { fetchApi } from '@/services/api-client';

export const CALENDAR_PATH = '/calendar';

export type CalendarEntry = {
  id: string;
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  allDay: boolean;
  location?: string;
  category: string;
  schoolYearId: string;
  schoolYearCode?: string;
  schoolYearName?: string;
  audience?: string;
  tags: string[];
  published: boolean;
};

export type CalendarCategory = {
  id: string;
  name: string;
  color: string;
  entryCount: number;
};

export type CreateCalendarEntryData = {
  title: string;
  description?: string;
  startDate: string;
  endDate: string;
  allDay?: boolean;
  location?: string;
  category: string;
  schoolYearId?: string;
  audience?: string;
  tags?: string[];
  published?: boolean;
};

export type UpdateCalendarEntryData = Partial<CreateCalendarEntryData>;

type CalendarListResponse = {
  data: CalendarEntry[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

export const calendarApi = {
  getAll: (page = 1, limit = 50, category?: string, year?: number, schoolYearId?: string) => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (category) params.append('category', category);
    if (year) params.append('year', year.toString());
    if (schoolYearId) params.append('schoolYearId', schoolYearId);
    return fetchApi<CalendarListResponse>(`${CALENDAR_PATH}/admin?${params}`);
  },
  getById: (id: string) => fetchApi<CalendarEntry>(`${CALENDAR_PATH}/admin/by-id/${id}`),
  create: (data: CreateCalendarEntryData) =>
    fetchApi<CalendarEntry>(CALENDAR_PATH, { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: UpdateCalendarEntryData) =>
    fetchApi<CalendarEntry>(`${CALENDAR_PATH}/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) => fetchApi<{ success: boolean }>(`${CALENDAR_PATH}/${id}`, { method: 'DELETE' }),
  togglePublished: (id: string) => fetchApi<CalendarEntry>(`${CALENDAR_PATH}/${id}/publish`, { method: 'PATCH' }),
  getCategories: () => fetchApi<CalendarCategory[]>(`${CALENDAR_PATH}/categories`),
  getManagedCategories: () => fetchApi<CalendarCategory[]>(`${CALENDAR_PATH}/admin/categories`),
  createCategory: (data: { name: string; color?: string }) =>
    fetchApi<CalendarCategory>(`${CALENDAR_PATH}/admin/categories`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateCategory: (id: string, data: { name: string; color?: string }) =>
    fetchApi<CalendarCategory>(`${CALENDAR_PATH}/admin/categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  deleteCategory: (id: string) =>
    fetchApi<{ success: boolean }>(`${CALENDAR_PATH}/admin/categories/${id}`, { method: 'DELETE' }),
};
